import type { ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@pracownik/ui";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useDocumentTitle } from "@/lib/brand";

interface AuthLayoutProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/** Shared chrome for sign-in, sign-up and recovery screens. */
export function AuthLayout({ title, description, icon, children, footer }: AuthLayoutProps) {
  const { t } = useTranslation("common");
  useDocumentTitle(title);
  return (
    <div className="relative flex min-h-dvh flex-col bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-dots opacity-60 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]"
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[40vh] bg-glow" />

      <header className="relative mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6">
        <BrandLogo />
        <ThemeToggle />
      </header>

      <main className="relative flex flex-1 items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md gap-6 rounded-2xl p-1 shadow-elevated animate-scale-in">
          <CardHeader className="items-center pt-6 text-center">
            {icon && (
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-primary-soft text-primary [&>svg]:size-6">
                {icon}
              </div>
            )}
            <CardTitle className="text-2xl">{title}</CardTitle>
            {description && (
              <CardDescription className="text-[15px]">{description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="pb-6">{children}</CardContent>
        </Card>
      </main>

      <footer className="relative mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-xs text-text-tertiary sm:px-6">
        <span>{footer ?? `© ${new Date().getFullYear()} ${t("appName")}`}</span>
        <nav className="flex gap-4">
          <Link to="/terms" className="hover:text-foreground">
            {t("footer.terms")}
          </Link>
          <Link to="/privacy" className="hover:text-foreground">
            {t("footer.privacy")}
          </Link>
        </nav>
      </footer>
    </div>
  );
}
